import { useEffect, useState, useRef } from "react";
import {
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Space,
  Select,
  Switch,
  Upload,
  Image,
  Button,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { toastError, toastSuccess } from "helpers/toasters";
import {
  addPublicAuction,
  updatePublicAuction,
  getAuctionTypes,
} from "Network";
import { handleAllowNumbersOnly } from "helpers/helpfulFunctions";

// components
import PrimaryButton from "../SubComponents/Buttons/PrimaryButton";
import SecondaryButton from "../SubComponents/Buttons/SecondaryButton";
import { baseUrl } from "services";

const MazadModal = ({
  isAuctionModalVisible,
  mode,
  rowData,
  SubHeader,
  onCancel,
  primaryBtnName,
  secondaryBtnName,
  setRenderMe,
}) => {
  const [types, setTypes] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form] = Form.useForm();
  const [imageFile, setImageFile] = useState(null);
  const [imageUrl, setImageUrl] = useState("");
  const [hasChanges, setHasChanges] = useState(false);
  const initialFormValuesRef = useRef(null);

  const watchedDescription = Form.useWatch("Description", form);
  const watchedDescriptionAr = Form.useWatch("DescriptionAr", form);
  const watchedTypeId = Form.useWatch("TypeId", form);
  const watchedQuantity = Form.useWatch("Quantity", form);
  const watchedIsActive = Form.useWatch("IsActive", form);

  useEffect(() => {
    if (mode === "edit") {
      const initial = initialFormValuesRef.current;
      if (!initial) return;

      const fieldsChanged =
        watchedDescription !== initial.Description ||
        watchedDescriptionAr !== initial.DescriptionAr ||
        watchedTypeId !== initial.TypeId ||
        watchedQuantity !== initial.Quantity ||
        watchedIsActive !== initial.IsActive;

      const imageChanged = !!imageFile;

      setHasChanges(fieldsChanged || imageChanged);
    }
  }, [
    watchedDescription,
    watchedDescriptionAr,
    watchedTypeId,
    watchedQuantity,
    watchedIsActive,
    imageFile,
    mode,
  ]);

  const validateQuantity = (_, value) => {
    if (!value && value !== 0) {
      return Promise.reject("Please, Enter the quantity");
    } else if (value <= 0) {
      return Promise.reject("Quantity should be greater than 0");
    } else if (value > 1000000) {
      return Promise.reject(
        "Maximum quantity should be less than or equal to one million"
      );
    } else {
      return Promise.resolve();
    }
  };

  const handleFinish = (values) => {
    console.log(imageFile);
    console.log(imageUrl);
    if (isSubmitting) return;
    setIsSubmitting(true);

    if (!imageFile && !imageUrl) {
      toastError("Please upload an image");
      setIsSubmitting(false);
      return;
    }

    // if (mode === "edit" && (!imageFile || !imageUrl)) {
    // }

    const formData = new FormData();

    formData.append("Description", values.Description);
    formData.append("DescriptionAr", values.DescriptionAr);
    formData.append("TypeId", values.TypeId);
    formData.append("Quantity", values.Quantity);
    formData.append("IsActive", values.IsActive);

    if (imageFile) {
      formData.append("Image", imageFile);
    } else {
      formData.append("Image", null);
    }

    if (mode === "edit") {
      formData.append("Id", rowData.id);
    }

    const apiCall = mode === "add" ? addPublicAuction : updatePublicAuction;

    apiCall(
      formData,
      (res) => {
        setIsSubmitting(false);
        if (res.success) {
          toastSuccess(res.message);
          onCancel();
          setRenderMe((pre) => !pre);
        } else {
          toastError(res.message);
        }
      },
      (res) => {
        setIsSubmitting(false);
        toastError(res.message);
      },
      true
    );
  };

  const getTypes = () => {
    getAuctionTypes(
      (res) => {
        if (res.success) {
          setTypes(res.data || []);
        } else {
          toastError(res.message);
        }
      },
      (res) => {
        toastError(res.message);
      },
      true
    );
  };

  const validateImage = (file) => {
    const ALLOWED_TYPES = ["image/png", "image/jpeg", "image/jpg"];

    const isValidType = ALLOWED_TYPES.includes(file.type);

    if (!isValidType) {
      toastError("Please upload png or jpeg images");
      return Upload.LIST_IGNORE;
    }

    setImageFile(file);
    setImageUrl(URL.createObjectURL(file));

    return false;
  };

  useEffect(() => {
    if (isAuctionModalVisible) {
      getTypes();
      if (mode === "edit" && rowData) {
        const initialValues = {
          Description: rowData.description,
          DescriptionAr: rowData.descriptionAr,
          TypeId: rowData.typeId,
          Quantity: rowData.quantity,
          IsActive: rowData.isActive,
          Image: rowData.imageUrl ? baseUrl + rowData.imageUrl : "",
        };

        initialFormValuesRef.current = initialValues;

        form.setFieldsValue(initialValues);

        if (rowData.imageUrl) {
          setImageUrl(baseUrl + rowData.imageUrl);
        }
      } else {
        form.resetFields();
        setImageFile(null);
        setImageUrl("");
        initialFormValuesRef.current = null;
        setHasChanges(false);
      }
    }
  }, [isAuctionModalVisible, mode, rowData]);

  return (
    <Modal
      open={isAuctionModalVisible}
      onCancel={onCancel}
      footer={null}
      width={500}
      centered
      className="msg-modal"
      title={SubHeader}
    >
      <div className="auction-modal__content d-flex flex-column">
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          initialValues={{
            Description: "",
            DescriptionAr: "",
            TypeId: undefined,
            Quantity: "",
            IsActive: true,
            Image: "",
          }}
        >
          <Col span={24}>
            <Form.Item
              label="Description (English)"
              name="Description"
              rules={[
                {
                  required: true,
                  message: "Please, Enter the description in English",
                },
                {
                  pattern: /^[A-Za-z0-9\s.,!?(){}[\]'"%-]+$/,
                  message:
                    "Only English letters, numbers, and common symbols are allowed",
                },
              ]}
            >
              <Input placeholder="Enter description in English" />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              label="Description (Arabic)"
              name="DescriptionAr"
              rules={[
                {
                  required: true,
                  message: "Please, Enter the description in Arabic",
                },
                {
                  pattern: /^[\u0600-\u06FF0-9\s.,!?؟،؛:(){}[\]'"%-]+$/,
                  message:
                    "Only Arabic letters, numbers, and common symbols are allowed",
                },
              ]}
            >
              <Input placeholder="Enter description in Arabic" />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item
              label="Type"
              name="TypeId"
              rules={[
                {
                  required: true,
                  message: "Please, Select the type",
                },
              ]}
            >
              {/* <Select
                placeholder="Select type"
                options={types?.map((type) => ({
                  label: type.nameEn || type.name,
                  value: type.id,
                }))}
              /> */}
              <Select
                showSearch
                placeholder="Select type"
                optionFilterProp="label"
                options={types?.map((type) => ({
                  label: type.nameEn || type.name,
                  value: type.id,
                }))}
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              label="Quantity (KG)"
              name="Quantity"
              rules={[
                {
                  validator: validateQuantity,
                },
              ]}
            >
              <InputNumber
                placeholder="Enter quantity"
                style={{ width: "100%" }}
                onKeyPress={handleAllowNumbersOnly}
              />
            </Form.Item>
          </Col>

          <Form.Item label="Image">
            <Upload
              disabled={isSubmitting}
              accept="image/png, image/jpg, image/jpeg"
              showUploadList={false}
              beforeUpload={validateImage}
            >
              <Button icon={<UploadOutlined />}>Upload Image</Button>
            </Upload>

            {imageUrl && (
              <div style={{ marginTop: 16 }}>
                <Image
                  src={imageUrl}
                  alt="Preview"
                  width={200}
                  height={150}
                  style={{ objectFit: "cover", borderRadius: 8 }}
                />
              </div>
            )}
          </Form.Item>

          <Col span={24}>
            <Form.Item
              label="Is Active"
              name="IsActive"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </Col>

          <div className="d-flex justify-content-center">
            <Space>
              <SecondaryButton
                onClick={onCancel}
                text={secondaryBtnName || "Cancel"}
                buttonName={"Cancel"}
              />
              <PrimaryButton
                buttonName={mode === "edit" ? "Edit Auction" : "Add Auction"}
                type="submit"
                loading={isSubmitting}
                disableMe={mode === "edit" && !hasChanges}
                text={primaryBtnName || "Submit"}
              />
            </Space>
          </div>
        </Form>
      </div>
    </Modal>
  );
};

export default MazadModal;
